<template>
  <TabView v-model:activeIndex="active" class="tabview">
    <TabPanel v-for="(device, index) in devices" :key="index">
      <template #header>
        <div
          :class="[
            `flex align-items-center gap-2 ${
              device.valid ? 'text-primary' : 'shake text-red-400'
            }`,
          ]"
        >
          <i :class="device.icon"></i>
          {{ device.title }}
        </div>
      </template>

      <div :class="[`container ${device.type}-container`]">
        <div
          v-for="n in 6"
          :key="n"
          :class="[`area area-${n} ${areaIsSelected(n) ? 'bg-blue-300' : ''}`]"
          @mousedown="selectArea(n)"
          @mouseenter="mouseenter($event, n)"
        ></div>
      </div>

      <small v-if="!device.valid" class="p-error">
        No es una combinación válida
      </small>
    </TabPanel>
  </TabView>
</template>

<script setup>
import { ref } from "vue";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";

// #region Props, Emits
// #endregion

// #region Params, Ref, Reactives, Computed
const devices = ref([
  {
    type: "desktop",
    icon: "pi pi-desktop",
    title: "Escritorio",
    selectedAreas: [],
    valid: true,
  },
  {
    type: "tablet",
    icon: "pi pi-tablet",
    title: "Tablet",
    selectedAreas: [],
    valid: true,
  },
  {
    type: "mobile",
    icon: "pi pi-mobile",
    title: "Móvil",
    selectedAreas: [],
    valid: true,
  },
]);
const active = ref(0);
// #endregion

// #region Functions
function loadAreas(areas) {
  devices.value = areas;
}

function selectArea(area) {
  const device = devices.value[active.value];
  const index = device.selectedAreas.indexOf(area);

  if (index === -1) {
    device.selectedAreas.push(area);
  } else {
    device.selectedAreas.splice(index, 1);
  }
}

function areaIsSelected(area) {
  const device = devices.value[active.value];

  return device.selectedAreas.includes(area);
}

function mouseenter(event, index) {
  // if mouse is clicked while enter the div, select the area
  if (event.buttons === 1 && !areaIsSelected(index)) {
    selectArea(index);
  }
}

function validateAreas() {
  const desktopAreas = devices.value[0].selectedAreas;
  desktopAreas.sort((a, b) => a - b);

  devices.value[0].valid = validateDesktopAreas(desktopAreas);

  const tabletAreas = devices.value[1].selectedAreas;
  tabletAreas.sort((a, b) => a - b);

  devices.value[1].valid = validateTabletAreas(tabletAreas);

  const mobileAreas = devices.value[2].selectedAreas;
  mobileAreas.sort((a, b) => a - b);

  devices.value[2].valid = validateMobileAreas(devices.value[2].selectedAreas);

  let allValid = true;

  devices.value.forEach((device) => {
    if (!device.valid) {
      allValid = false;
    }
  });

  return allValid;
}

function validateDesktopAreas(areas) {
  let valid = true;

  switch (areas.length) {
    case 2:
      valid =
        (areas[0] == 1 && areas[1] == 2) ||
        (areas[0] == 2 && areas[1] == 3) ||
        (areas[0] == 4 && areas[1] == 5) ||
        (areas[0] == 5 && areas[1] == 6) ||
        (areas[0] == 1 && areas[1] == 4) ||
        (areas[0] == 2 && areas[1] == 5) ||
        (areas[0] == 3 && areas[1] == 6);

      break;

    case 3:
      valid =
        (areas[0] == 1 && areas[1] == 2 && areas[2] == 3) ||
        (areas[0] == 4 && areas[1] == 5 && areas[2] == 6);

      break;

    case 4:
      valid =
        (areas[0] == 1 && areas[1] == 2 && areas[2] == 4 && areas[3] == 5) ||
        (areas[0] == 2 && areas[1] == 3 && areas[2] == 5 && areas[3] == 6);

      break;

    case 5:
      valid = false;

      break;

    default:
      valid = true;

      break;
  }

  return valid;
}

function validateTabletAreas(areas) {
  let valid = true;

  switch (areas.length) {
    case 2:
      valid =
        (areas[0] == 1 && areas[1] == 2) ||
        (areas[0] == 3 && areas[1] == 4) ||
        (areas[0] == 5 && areas[1] == 5) ||
        (areas[0] == 1 && areas[1] == 3) ||
        (areas[0] == 3 && areas[1] == 5) ||
        (areas[0] == 2 && areas[1] == 4) ||
        (areas[0] == 4 && areas[1] == 6);

      break;

    case 3:
      valid =
        (areas[0] == 1 && areas[1] == 3 && areas[2] == 5) ||
        (areas[0] == 2 && areas[1] == 4 && areas[2] == 6);

      break;

    case 4:
      valid =
        (areas[0] == 1 && areas[1] == 2 && areas[2] == 3 && areas[3] == 4) ||
        (areas[0] == 3 && areas[1] == 4 && areas[2] == 5 && areas[3] == 6);

      break;

    case 5:
      valid = false;

      break;

    default:
      valid = true;

      break;
  }

  return valid;
}

function validateMobileAreas(areas) {
  let valid = true;

  switch (areas.length) {
    case 2:
      valid =
        (areas[0] == 1 && areas[1] == 2) ||
        (areas[0] == 2 && areas[1] == 3) ||
        (areas[0] == 3 && areas[1] == 4) ||
        (areas[0] == 4 && areas[1] == 5) ||
        (areas[0] == 5 && areas[1] == 6);

      break;

    case 3:
      valid =
        (areas[0] == 1 && areas[1] == 2 && areas[2] == 3) ||
        (areas[0] == 2 && areas[1] == 3 && areas[2] == 4) ||
        (areas[0] == 3 && areas[1] == 4 && areas[2] == 5) ||
        (areas[0] == 4 && areas[1] == 5 && areas[2] == 6);

      break;

    case 4:
      valid =
        (areas[0] == 1 && areas[1] == 2 && areas[2] == 3 && areas[3] == 4) ||
        (areas[0] == 2 && areas[1] == 3 && areas[2] == 4 && areas[3] == 5) ||
        (areas[0] == 3 && areas[1] == 4 && areas[2] == 5 && areas[3] == 6);

      break;

    case 5:
      valid =
        (areas[0] == 1 &&
          areas[1] == 2 &&
          areas[2] == 3 &&
          areas[3] == 4 &&
          areas[4] == 5) ||
        (areas[0] == 2 &&
          areas[1] == 3 &&
          areas[2] == 4 &&
          areas[3] == 5 &&
          areas[4] == 6);

      break;

    default:
      valid = true;

      break;
  }

  return valid;
}
// #endregion

// #region Effects, Hooks
// #endregion

// #region Provide, Expose
defineExpose({
  devices,
  validateAreas,
  loadAreas,
});
// #endregion
</script>

<style lang="scss" scoped>
@import "@/styles/_vars.scss";

.tabview {
  :deep(.p-tabview-panels) {
    padding-bottom: 0;
  }
}

.shake {
  animation: shake 0.5s;

  @keyframes shake {
    0% {
      transform: translateX(0);
    }

    25% {
      transform: translateX(-3px);
    }

    50% {
      transform: translateX(3px);
    }

    75% {
      transform: translateX(-3px);
    }

    100% {
      transform: translateX(0);
    }
  }
}

.container {
  display: grid;
  gap: 10px 10px;
  grid-auto-flow: row;
  height: 20rem;

  &.desktop-container {
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    grid-template-areas:
      "area-1 area-2 area-3"
      "area-4 area-5 area-6";
  }

  &.tablet-container {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr;
    grid-template-areas:
      "area-1 area-2"
      "area-3 area-4"
      "area-5 area-6";
  }

  &.mobile-container {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;
    grid-template-areas:
      "area-1"
      "area-2"
      "area-3"
      "area-4"
      "area-5"
      "area-6";
  }

  .area {
    background-color: rgba($primary, 10%);
    border: 1px solid $primary;
    border-radius: 0.5rem;
    transition: $transition;
    cursor: pointer;

    &-1 {
      grid-area: area-1;
    }

    &-2 {
      grid-area: area-2;
    }

    &-3 {
      grid-area: area-3;
    }

    &-4 {
      grid-area: area-4;
    }

    &-5 {
      grid-area: area-5;
    }

    &-6 {
      grid-area: area-6;
    }

    &:hover {
      background-color: rgba($primary, 20%);
    }
  }
}
</style>
